import React, { useState, useEffect } from 'react';
import { Icon } from '../Common/Icons';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import { membershipInvoiceAPI, membershipPaymentAPI } from '../../../services/membershipApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';
import {
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CloudArrowUpIcon,
  CreditCardIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const PublicMembershipPaymentSubmit = () => {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paymentTermsAgreed, setPaymentTermsAgreed] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Payment form state
  const [paymentData, setPaymentData] = useState({
    actualAmount: '',
    paymentMethod: 'Online', // Default tab
    paymentDate: new Date().toISOString().split('T')[0],
    referenceNumber: '',
    bankName: '',
    notes: ''
  });
  
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [downloadingProforma, setDownloadingProforma] = useState(false);

  useEffect(() => {
    fetchInvoiceByToken();
  }, [token]);

  const fetchInvoiceByToken = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await membershipInvoiceAPI.getPublicByToken(token);
      const data = response.data.data;
      setInvoice(data);
      
      setPaymentData(prev => ({
        ...prev,
        actualAmount: data.total || '',
        referenceNumber: data.invoiceNumber || '',
        paymentMethod: data.paymentLinkMethod === 'bank_transfer' ? 'Bank Transfer' : 'Online'
      }));
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setError(error.response?.data?.message || 'Invalid or expired payment link. Please contact the institution.');
    } finally {
      setLoading(false);
    }
  };

  const downloadProforma = async () => {
    setDownloadingProforma(true);
    try {
      const res = await membershipInvoiceAPI.downloadPublicProforma(token);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      a.download = `Proforma-${String(invoice?.invoiceNumber || 'invoice').replace(/[\/\\]/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Could not download the proforma invoice. Please try again.');
    } finally {
      setDownloadingProforma(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    if (!paymentTermsAgreed) {
      toast.error('Please agree to the terms before proceeding.');
      return;
    }

    try {
      setSubmitting(true);

      if (paymentData.paymentMethod === 'Online') {
        // ── Card payment: redirect to Paycorp gateway ──
        const returnUrl = `${window.location.origin}/membership/payment/${token}/callback`;
        const cancelUrl = window.location.href;

        const response = await membershipPaymentAPI.initiatePublicCard(token, {
          returnUrl,
          cancelUrl
        });

        const paymentPageUrl = response.data?.data?.paymentPageUrl || response.data?.data?.paymentUrl;
        if (response.data?.success && paymentPageUrl) {
          window.location.href = paymentPageUrl;
        } else {
          throw new Error('Failed to initiate payment gateway');
        }

      } else if (paymentData.paymentMethod === 'Bank Transfer') {
        // ── Bank transfer: upload slip and submit ──
        if (!receiptFile) {
          toast.error('Please upload your bank transfer slip.');
          setSubmitting(false);
          return;
        }

        const formData = new FormData();
        formData.append('receipt', receiptFile);
        formData.append('paymentMethod', 'bank_transfer');
        formData.append('bankName', paymentData.bankName);
        formData.append('referenceNumber', paymentData.referenceNumber);

        await membershipInvoiceAPI.submitPublicBankTransfer(token, formData);
        setIsSubmitted(true);

      } else {
        toast.error('Please select a payment method.');
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Unable to process payment. Please try again later.';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };


  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full text-center border border-emerald-100">
          <CheckCircleIcon className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-medium text-gray-900 mb-2">Thank You!</h2>
          <p className="text-sm text-gray-600">Your bank transfer slip has been uploaded successfully and sent for approval.</p>
          <p className="text-xs text-gray-500 mt-2">You will receive a confirmation email once verified by our finance team.</p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner message="Loading payment information" />;

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full text-center border border-violet-100">
          <ExclamationCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-medium text-gray-900 mb-2">Invalid Link</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full px-6 py-3 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-all font-medium shadow-lg shadow-violet-200"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] py-12 px-4 font-sans">
      <div className="max-w-4xl mx-auto">
        {/* Brand Header */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <div className="w-12 h-12 bg-violet-600 rounded-xl flex items-center justify-center text-white font-medium text-2xl shadow-lg shadow-violet-200">S</div>
          <div>
            <h1 className="text-2xl font-medium text-gray-900">SLIM ERP</h1>
            <p className="text-xs text-violet-600 tracking-[0.2em] font-medium">Membership Payment Portal</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Summary Panel */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-sm font-medium text-gray-400 mb-6">Invoice Summary</h2>
              
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-400 font-medium mb-1">Invoice Number</p>
                  <p className="text-lg font-medium text-gray-900">{invoice?.invoiceNumber}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-400 font-medium mb-1">Member</p>
                  <p className="text-sm font-medium text-gray-800">{invoice?.memberName}</p>
                  <p className="text-xs text-gray-500">{invoice?.memberEmail}</p>
                </div>

                <div className="pt-4 border-t border-gray-50">
                  <div className="flex justify-between items-end">
                    <p className="text-xs text-gray-400 font-medium">Total Due</p>
                    <p className="text-3xl font-medium text-violet-600 leading-none">
                      <span className="text-sm font-medium mr-1">{invoice?.currency}</span>
                      {invoice?.total?.toLocaleString()}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={downloadProforma}
                  disabled={downloadingProforma}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 disabled:opacity-60 transition-colors"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  {downloadingProforma ? 'Preparing PDF…' : 'Download Proforma Invoice'}
                </button>
              </div>
            </div>

            {/* In-person payment option */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10 text-amber-600">
                <svg className="w-20 h-20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-amber-700"><Icon name="building" className="w-4 h-4" /></div>
                <div className="text-left">
                  <h3 className="text-sm font-medium text-amber-900 mb-1">Pay in Person</h3>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    You can also make a <span className="font-medium">cash or card payment</span> by visiting the <span className="font-medium">SLIM Cashier Center</span> directly.
                  </p>
                  <p className="text-xs text-amber-600 mt-2">
                    Please bring your invoice number <span className="tabular-nums font-medium">{invoice?.invoiceNumber}</span> when you visit.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Panel */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8 flex flex-col items-center text-center">
              {(!invoice?.paymentLinkMethod || invoice?.paymentLinkMethod === 'both') && (
                <div className="flex gap-4 w-full mb-4">
                  <button
                    type="button"
                    onClick={() => setPaymentData(p => ({ ...p, paymentMethod: 'Online' }))}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all ${
 paymentData.paymentMethod === 'Online'
 ? 'bg-violet-100 text-violet-700 border-2 border-violet-500 shadow-sm'
 : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
 }`}
                  >
                    Online Card Payment
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentData(p => ({ ...p, paymentMethod: 'Bank Transfer' }))}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all ${
 paymentData.paymentMethod === 'Bank Transfer'
 ? 'bg-violet-100 text-violet-700 border-2 border-violet-500 shadow-sm'
 : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
 }`}
                  >
                    Bank Transfer
                  </button>
                </div>
              )}

              {/* Cash/Card message */}
              <div className="w-full text-center py-3 px-4 mb-6 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-900 font-medium shadow-sm">
                If you want to do a cash or card payment, please visit SLIM.
              </div>

              {paymentData.paymentMethod === 'Online' ? (
                <>
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                    <CreditCardIcon className="w-10 h-10 text-violet-500" />
                  </div>
                  <h2 className="text-2xl font-medium text-gray-900 mb-2">Credit / Debit Card</h2>
                  <p className="text-sm text-gray-500 mb-8">
                    Pay securely using your Visa, Mastercard, or Amex.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                    <CloudArrowUpIcon className="w-10 h-10 text-violet-500" />
                  </div>
                  <h2 className="text-2xl font-medium text-gray-900 mb-2">Upload Transfer Slip</h2>
                  <p className="text-sm text-gray-500 mb-6">
                    Please upload your bank transfer slip or screenshot.
                  </p>
                  
                  {invoice?.bankAccounts?.length > 0 && (
                    <div className="w-full mb-8 text-left bg-violet-50 rounded-xl p-6 border border-violet-100">
                      <h4 className="text-xs font-medium text-violet-900 mb-4 flex items-center gap-2">
                        <BanknotesIcon className="w-4 h-4" /> Company Bank Accounts
                      </h4>
                      <div className="space-y-4">
                        {invoice.bankAccounts.map((acc, idx) => (
                          <div key={idx} className="bg-white p-4 rounded-xl border border-violet-100/50 shadow-sm">
                            <p className="font-medium text-gray-900 text-sm mb-1">{acc.bankName} <span className="text-gray-400 font-normal">({acc.branchName || 'Head Office'})</span></p>
                            <div className="flex justify-between items-center">
                              <p className="tabular-nums text-violet-700 font-medium">{acc.accountNumber}</p>
                              <p className="text-xs text-gray-500 font-medium">{acc.accountName}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <form onSubmit={handleSubmitPayment} className="w-full space-y-6">
                {paymentData.paymentMethod === 'Bank Transfer' && (
                  <div className="space-y-4 mb-6">
                    <ProofAttachmentDropzone
                      file={receiptFile}
                      onChange={setReceiptFile}
                      required
                      hint={null}
                    />
                    <div className="text-left">
                      <label className="block text-xs font-medium text-gray-700 mb-2">Bank Name (Optional)</label>
                      <input
                        type="text"
                        name="bankName"
                        value={paymentData.bankName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none"
                        placeholder="e.g. Commercial Bank"
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl text-left">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={paymentTermsAgreed}
                    onChange={(e) => setPaymentTermsAgreed(e.target.checked)}
                    className="mt-1 w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                  />
                  <label htmlFor="terms" className="text-xs text-gray-500 leading-relaxed font-medium">
                    {paymentData.paymentMethod === 'Online' 
                      ? 'I agree to the payment terms and conditions and understand that this transaction is secure.'
                      : 'I confirm that this slip is authentic and I agree to the terms.'}
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-gray-900 text-white rounded-xl font-medium text-sm hover:bg-violet-600 transition-all disabled:bg-gray-200 disabled:cursor-not-allowed shadow-xl shadow-gray-200 hover:shadow-violet-200 flex items-center justify-center gap-3"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Redirecting...
                    </>
                  ) : (
                    <>
                      {paymentData.paymentMethod === 'Online' ? <CreditCardIcon className="w-5 h-5" /> : <CloudArrowUpIcon className="w-5 h-5" />}
                      {paymentData.paymentMethod === 'Online' ? 'Pay Securely Now' : 'Submit for Approval'}
                    </>
                  )}
                </button>
              </form>

              <div className="mt-8 flex items-center justify-center gap-4 opacity-40 grayscale">
                <img src="https://upload.wikimedia.org/wikipedia/commons/d/d6/Visa_2021.svg" alt="Visa" className="h-4" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-6" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/American_Express_logo_%282018%29.svg" alt="Amex" className="h-6" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicMembershipPaymentSubmit;
